import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import type { ShoppingList } from "@graphql/generated/types";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { isMarketWithLoyalty } from "@lib/utils/loyalty";
import {
    addToMyShoppingList,
    deleteMyShoppingList,
} from "@lib/utils/shoppingList-utility/shoppingListUtility";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import { getUser } from "@lib/utils/user-api/getUserWithErrorHandler";
import {
    useAddProductAndRemoveOldestFromWishlist,
    useAddProductToWishlist,
} from "@lib/commerce/hooks/wishlistLineItemOperations";
import {
    useDeleteMyWishlist,
    useGetMyActiveShoppingLists,
} from "@lib/commerce/hooks/wishlistOperations";
import { useGetMyActiveData, useRecalculateCart } from "@lib/commerce/hooks/cartOperations";
import { useGetMyProfile } from "@store/hooks";
import { useStore, useStoreApi } from "@store/index";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";
import { useAuth } from "@lib/auth/useAuth";
import { getDatadogRum } from "@lib/monitoring/datadogHelper";

type LogInValues = {
    email: string;
    password: string;
};
type LogInActions = {
    setSubmitting?: (isSubmitting: boolean) => void;
};

type UseLoginParams = {
    onSuccess: () => void;
    onFail?: (error: number) => void;
    isModalLogin?: boolean;
};

const useLogIn = ({ onSuccess, onFail, isModalLogin = true }: UseLoginParams) => {
    const [didLogIn, setDidLogIn] = useState(false);
    const locale = useLocale();
    const { showModal } = useGlobalModalContext();
    const setMasterProducts = useStore((state) => state.setMasterProducts);
    const setWishlistMasterProducts = useStore((state) => state.setWishlistMasterProducts);
    const setCustomer = useStore((state) => state.setCustomer);
    const storeApi = useStoreApi();
    const { refetch } = useGetMyProfile();
    const { session, signOut, signIn } = useAuth();
    const { get: getMicrocopy } = useMicrocopy();
    const errorToaster = useErrorToast();
    const executeGetMyActiveData = useGetMyActiveData();
    const executeGetMyActiveWishlists = useGetMyActiveShoppingLists();
    const executeDeleteWishlist = useDeleteMyWishlist();
    const executeAddProductToWishlist = useAddProductToWishlist();
    const executeAddProductAndRemoveOldestFromWishlist = useAddProductAndRemoveOldestFromWishlist();
    const recalculateCart = useRecalculateCart();

    async function handleMergingCustomerShoppingLists(
        shoppingLists: ShoppingList[]
    ): Promise<void> {
        const userItems = shoppingLists?.[0]?.lineItems;
        const anonymousItems = shoppingLists?.slice(1);

        const anonymousLineItems = anonymousItems?.map(({ lineItems }) => lineItems).flat();

        if (!anonymousLineItems?.length) return;

        for (const lineItem of anonymousLineItems) {
            const { wishlist } = storeApi.getState().data.me.wishlistInfo;

            const foundItem = userItems.find(({ productId }) => productId === lineItem.productId);
            const shouldAddToWishlist = !foundItem && lineItem.variant?.sku;

            if (shouldAddToWishlist) {
                await addToMyShoppingList(
                    locale,
                    Array.isArray(wishlist) ? wishlist[0] : wishlist,
                    lineItem.variant.sku,
                    executeAddProductToWishlist,
                    executeAddProductAndRemoveOldestFromWishlist,
                    lineItem.quantity
                );
            }
        }

        for (const list of anonymousItems) {
            await deleteMyShoppingList(
                locale,
                list,
                getMicrocopy,
                executeDeleteWishlist,
                errorToaster
            );
        }
    }

    const syncData = async () => {
        setMasterProducts(null);
        setWishlistMasterProducts(null);

        const { data } = await executeGetMyActiveWishlists();

        await handleMergingCustomerShoppingLists(
            data?.me?.shoppingLists?.results as ShoppingList[]
        );

        await executeGetMyActiveData();

        const newCart = storeApi.getState().data.me.cartInfo.cart;
        if (newCart) {
            await recalculateCart({
                cartId: newCart.id,
                cartVersion: newCart.version,
                storeKey: newCart.store.key,
                locale: locale,
            });
        }
        const userData = await getUser(signOut);

        getDatadogRum()?.setUser({
            id: userData?.ucid || "",
            email: userData?.email || "",
            name: `${userData?.firstName || ""} ${userData?.lastName || ""}`,
            locale: locale,
            accountCreated: userData?.accountCreatedUtc || "",
            loyaltyId: userData?.loyalty?.id || "",
        });

        setCustomer(userData);
        onSuccess();
        if (isMarketWithLoyalty && isModalLogin) {
            const { data } = await refetch();
            if (!data?.loyalty) {
                return showModal(ModalTypes.upgradeAccount);
            }
        }
    };

    async function logIn(values: LogInValues, actions?: LogInActions): Promise<void> {
        const result = await signIn("authenticated", {
            redirect: false,
            email: values["email"].trim(),
            password: values["password"],
            locale: locale,
            anonymousId: session?.anonymousId || null,
            accessToken: session?.accessToken || null,
            sessionId: session?.sessionId || null,
        });

        if (!result.error) {
            window.ometria?.identify(values["email"]);
            actions?.setSubmitting(false);
            setDidLogIn(true);
            return;
        }

        onFail?.(result.status);
        actions?.setSubmitting(false);
    }

    // Because we need to call syncData() after the SessionProvider has been updated with the new session
    // we need to set a trigger a seperate call rather than calling it directly from logIn().
    // Calling it directly from logIn() would mean the Urql client contains a reference to a stale session.
    useEffect(() => {
        if (!didLogIn) return;
        syncData().then(() => {
            // Reset trigger
            setDidLogIn(false);
        });
    }, [didLogIn]);

    return { logIn: logIn };
};

export default useLogIn;
